import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { CheckCircle2, AlertTriangle, XCircle, Activity, FileText, Calendar, Lightbulb, Shield, Stethoscope, Salad } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusStyles = {
    normal: { icon: CheckCircle2, badge: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', label: 'طبيعي' },
    abnormal: { icon: AlertTriangle, badge: 'bg-amber-100 text-amber-700', border: 'border-amber-200', label: 'غير طبيعي' },
    critical: { icon: XCircle, badge: 'bg-red-100 text-red-700', border: 'border-red-200', label: 'حرج' },
};

export default function SharedReportPage() {
    const { token } = useParams();
    const [report, setReport] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get(`${API}/shared/${token}`)
            .then((res) => setReport(res.data))
            .catch((err) => {
                setError(err.response?.status === 410
                    ? 'انتهت صلاحية رابط المشاركة. يرجى طلب رابط جديد من صاحب التقرير.'
                    : 'الرابط غير صالح أو تم إلغاؤه.');
            })
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return (
            <div dir="rtl" className="max-w-3xl mx-auto px-4 py-10">
                <Skeleton className="h-10 w-56 mb-6" />
                <Skeleton className="h-40 rounded-2xl mb-4" />
                <Skeleton className="h-80 rounded-2xl" />
            </div>
        );
    }

    if (error) {
        return (
            <div dir="rtl" className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
                <div className="text-center max-w-md" data-testid="shared-report-error">
                    <XCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-stone-800 mb-2">تعذر عرض التقرير</h1>
                    <p className="text-stone-500 mb-6">{error}</p>
                    <Link to="/" className="text-teal-600 font-medium hover:underline">الانتقال إلى نتيجتي الطبية</Link>
                </div>
            </div>
        );
    }

    const summary = report.ai_summary_ar || report.ai_summary;
    const recommendations = (report.recommendations_ar?.length ? report.recommendations_ar : report.recommendations) || [];
    const disclaimer = report.medical_disclaimer_ar || report.medical_disclaimer;
    const dateStr = report.upload_date ? new Date(report.upload_date).toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

    return (
        <div dir="rtl" className="min-h-screen bg-stone-50" data-testid="shared-report-page">
            <header className="bg-white border-b border-stone-200">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Stethoscope className="w-6 h-6 text-teal-600" />
                        <span className="font-bold text-stone-800 text-lg">نتيجتي الطبية</span>
                    </div>
                    <Badge className="bg-teal-50 text-teal-700 border border-teal-200">تقرير تمت مشاركته</Badge>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
                <div className="bg-white rounded-2xl border border-stone-200 p-6">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                        <FileText className="w-5 h-5 text-teal-600" />
                        <h1 className="text-xl font-bold text-stone-800">{report.panel_type || 'تقرير تحاليل طبية'}</h1>
                        {report.urgency_level === 'urgent' && (
                            <Badge className="bg-red-100 text-red-700">يتطلب متابعة عاجلة</Badge>
                        )}
                        {typeof report.severity_score === 'number' && (
                            <Badge className={
                                report.severity_score <= 3 ? 'bg-emerald-100 text-emerald-700' :
                                report.severity_score <= 6 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                            } data-testid="shared-severity-badge">
                                درجة الخطورة: {report.severity_score}/10
                            </Badge>
                        )}
                    </div>
                    {dateStr && (
                        <p className="text-sm text-stone-500 flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" /> {dateStr}
                        </p>
                    )}
                    {summary && (
                        <p className="mt-4 text-stone-700 leading-relaxed" data-testid="shared-report-summary">{summary}</p>
                    )}
                </div>

                {report.lab_values?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-stone-200 p-6">
                        <h2 className="text-base font-bold text-stone-800 mb-4 flex items-center gap-2">
                            <Activity className="w-5 h-5 text-teal-600" /> نتائج الفحوصات ({report.lab_values.length})
                        </h2>
                        <div className="space-y-2" data-testid="shared-lab-values">
                            {report.lab_values.map((lv, i) => {
                                const st = statusStyles[lv.status] || statusStyles.normal;
                                const Icon = st.icon;
                                return (
                                    <div key={i} className={`flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl border ${st.border} bg-stone-50/50`}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Icon className={`w-4 h-4 shrink-0 ${lv.status === 'critical' ? 'text-red-500' : lv.status === 'abnormal' ? 'text-amber-500' : 'text-emerald-500'}`} />
                                            <span className="font-medium text-stone-800 text-sm">{lv.name_ar || lv.name}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-bold text-stone-800 text-sm">{lv.value} <span className="font-normal text-stone-500">{lv.unit}</span></span>
                                            {lv.reference_range && <span className="text-xs text-stone-400">المرجع: {lv.reference_range}</span>}
                                            <Badge className={`${st.badge} border-0 text-xs`}>{st.label}</Badge>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {recommendations.length > 0 && (
                    <div className="bg-white rounded-2xl border border-stone-200 p-6">
                        <h2 className="text-base font-bold text-stone-800 mb-3 flex items-center gap-2">
                            <Lightbulb className="w-5 h-5 text-amber-500" /> التوصيات
                        </h2>
                        <ul className="space-y-2">
                            {recommendations.map((rec, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                                    <CheckCircle2 className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" /> {rec}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {report.recommended_specialists?.length > 0 && (
                    <div className="bg-white rounded-2xl border border-stone-200 p-6">
                        <h2 className="text-base font-bold text-stone-800 mb-3 flex items-center gap-2">
                            <Stethoscope className="w-5 h-5 text-sky-600" /> التخصصات الطبية الموصى بها
                        </h2>
                        <div className="space-y-2" data-testid="shared-specialists">
                            {report.recommended_specialists.map((sp, i) => {
                                const urg = (sp.urgency || '').toLowerCase();
                                const badge = urg === 'high' ? 'bg-red-100 text-red-700' : urg === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
                                const label = urg === 'high' ? 'أولوية عالية' : urg === 'medium' ? 'أولوية متوسطة' : 'روتيني';
                                return (
                                    <div key={i} className="p-3 rounded-xl border border-stone-200 bg-stone-50/50">
                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                                            <span className="font-medium text-stone-800 text-sm">{sp.specialty_ar || sp.specialty_en}</span>
                                            <Badge className={`${badge} border-0 text-xs`}>{label}</Badge>
                                        </div>
                                        {sp.reason && <p className="text-xs text-stone-600">{sp.reason}</p>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {report.nutrition_recommendations && (report.nutrition_recommendations.foods_to_eat?.length > 0 || report.nutrition_recommendations.foods_to_avoid?.length > 0) && (
                    <div className="bg-white rounded-2xl border border-stone-200 p-6">
                        <h2 className="text-base font-bold text-stone-800 mb-3 flex items-center gap-2">
                            <Salad className="w-5 h-5 text-lime-600" /> التوصيات الغذائية
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="shared-nutrition">
                            {report.nutrition_recommendations.foods_to_eat?.length > 0 && (
                                <div className="p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
                                    <p className="font-bold text-emerald-700 text-sm mb-2">✓ أطعمة يُنصح بها</p>
                                    <ul className="space-y-1.5">
                                        {report.nutrition_recommendations.foods_to_eat.map((f, i) => (
                                            <li key={i} className="text-xs text-stone-700">
                                                <span className="font-semibold">{f.item}</span>
                                                {f.reason && <span className="text-stone-500"> — {f.reason}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {report.nutrition_recommendations.foods_to_avoid?.length > 0 && (
                                <div className="p-3 rounded-xl bg-red-50/50 border border-red-100">
                                    <p className="font-bold text-red-700 text-sm mb-2">✕ أطعمة يُفضل تقليلها</p>
                                    <ul className="space-y-1.5">
                                        {report.nutrition_recommendations.foods_to_avoid.map((f, i) => (
                                            <li key={i} className="text-xs text-stone-700">
                                                <span className="font-semibold">{f.item}</span>
                                                {f.reason && <span className="text-stone-500"> — {f.reason}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        {report.nutrition_recommendations.general_tips?.length > 0 && (
                            <ul className="mt-3 space-y-1">
                                {report.nutrition_recommendations.general_tips.map((tip, i) => (
                                    <li key={i} className="text-xs text-stone-600">• {tip}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {disclaimer && (
                    <div className="flex items-start gap-2 text-xs text-stone-500 bg-stone-100 rounded-xl p-4">
                        <Shield className="w-4 h-4 shrink-0 mt-0.5" /> {disclaimer}
                    </div>
                )}

                <div className="text-center py-4">
                    <p className="text-sm text-stone-500 mb-2">هل لديك تقرير تحاليل تريد فهمه؟</p>
                    <Link to="/" className="inline-block bg-teal-500 hover:bg-teal-600 text-white font-medium px-6 py-2.5 rounded-full transition-colors" data-testid="shared-report-cta">
                        حلّل تقريرك مجاناً في نتيجتي الطبية
                    </Link>
                </div>
            </main>
        </div>
    );
}
